import { tryCatch } from "@utils/typescript/try-catch.ts";

enum EMAIL_AVAILABLE_RESPONSE_CODES {
  OK = "OK",
  NOT_AVAILABLE = "EMAIL_EXISTING",
}

interface ValidationPatterns {
  street: Record<string, string>;
  postalCode: Record<string, string>;
  vatId: Record<string, string>;
  customerNumber: string;
  phone: string;
  password: string;
}

interface RegistrationElements {
  sections: Map<string, HTMLElement>;
  inputFields: Map<string, HTMLInputElement>;
  radioButtons: Map<string, NodeListOf<HTMLInputElement>>;
  buttons: Map<string, HTMLButtonElement>;
  tabPanels: Map<string, HTMLDivElement>;
}

/**
 * Registration Manager - Handles multistep user registration process
 */
class RegistrationManager {
  private readonly form: HTMLFormElement;
  private readonly elements: RegistrationElements;
  private readonly validationPatterns: ValidationPatterns;

  private isSmasuRegistration: boolean = false;

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.validationPatterns = this.initializeValidationPatterns();
    this.elements = this.collectAllElements();

    if (this.validateElementsAvailability()) {
      this.initialize();
    }
  }

  /**
   * Static factory method to create and initialize registration manager
   */
  static create(): RegistrationManager | null {
    const form = document.querySelector<HTMLFormElement>(
      "form[data-registration]",
    );
    if (!form) return null;

    return new RegistrationManager(form);
  }

  /**
   * Initialize validation patterns for different countries
   */
  private initializeValidationPatterns(): ValidationPatterns {
    return {
      street: {
        LU: "^(?=^.{0,60}$)\\d+.*$",
        ES: "^.*\\d+.*$",
        default:
          "^(?=.{0,60}$)((?!packstation).)+\\d+\\.?[a-zA-Z0-9_\\-]*\\s*\\/?\\w*$",
      },
      postalCode: {
        LU: "^(?=^.{0,10}$)[0-9]{4}$",
        AT: "^(?=^.{0,10}$)[0-9]{4}$",
        CH: "^(?=^.{0,10}$)[0-9]{4}$",
        LI: "^(?=^.{0,10}$)[0-9]{4}$",
        BE: "^(?:(?:[1-9])(?:\\d{3}))$",
        NL: "^[1-9][0-9]{3} ?[a-zA-Z]{2}$",
        default: "^(?=^.{0,10}$)[0-9]{5}$",
      },
      vatId: {
        DE: "^DE[0-9]{9}$",
        AT: "^ATU[0-9]{8}$",
        CH: "^(CHE)?[-, ]?[0-9]{3}[\\., ]?[0-9]{3}[\\., ]?[0-9]{3}[ ]?(MWST|IVA|TVA)?$",
        LI: "^\\d{5}$",
        BE: "^[0-9]{10}$|^N.A.$",
        NL: "^NL[0-9]{9}B[0-9]{2}$",
        LU: "^LU[0-9]{8}$",
        ES: "^ES[A-Z][0-9]{7}(?:[0-9]|[A-Z])$",
        default: "",
      },
      customerNumber: "^\\d{0,10}$",
      phone: "^[\\d+\\-.\\(\\)\\/\\s]{0,16}$",
      password: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z\\d]).{8,}$",
    };
  }

  /**
   * Collect all required DOM elements
   */
  private collectAllElements(): RegistrationElements {
    const sectionNames = ["step1", "step2", "step3"];
    const inputFieldNames = [
      "email",
      "password",
      "passwordConfirmation",
      "frc-captcha-response",
      "customerProfile",
      "title",
      "givenName",
      "familyName",
      "phone",
      "b2b_name",
      "b2b_name2",
      "b2b_vatId",
      "b2b_smasuPassword",
      "b2b_street",
      "b2b_postalCode",
      "b2b_city",
      "b2b_nameAdditional",
      "b2b_countryCode",
      "b2b_industryName",
      "b2b_customerNumber",
      "b2c_street",
      "b2c_nameAdditional",
      "b2c_postalCode",
      "b2c_city",
      "b2c_countryCode",
      "b2c_customerNumber",
      "subscribeNewsletter",
      "terms",
      "gotoUrl",
      "baseUrl",
    ];
    const radioButtonNames = ["title"];
    const buttonNames = ["step1-submit", "step2-submit", "step3-submit"];
    const tabPanelNames = ["panel-1", "panel-2"];

    return {
      sections: this.collectElements<HTMLElement>(sectionNames),
      inputFields: this.collectElements<HTMLInputElement>(inputFieldNames),
      radioButtons: this.collectRadioButtons(radioButtonNames),
      buttons: this.collectElements<HTMLButtonElement>(buttonNames),
      tabPanels: this.collectElements<HTMLDivElement>(tabPanelNames),
    };
  }

  /**
   * Generic method to collect DOM elements by name or id
   */
  private collectElements<TElement extends Element>(
    elementNames: string[],
  ): Map<string, TElement> {
    const elements = new Map<string, TElement>();
    elementNames.forEach((elementName) => {
      const element = this.form.querySelector<TElement>(
        `[name="${elementName}"], [id="${elementName}"]`,
      );
      elements.set(elementName, element as TElement);
    });
    return elements;
  }

  /**
   * Collect radio button elements
   */
  private collectRadioButtons(
    elementNames: string[],
  ): Map<string, NodeListOf<HTMLInputElement>> {
    const elements = new Map<string, NodeListOf<HTMLInputElement>>();
    elementNames.forEach((elementName) => {
      const radioButtons: NodeListOf<HTMLInputElement> =
        this.form.querySelectorAll(
          `[name="${elementName}"], [id="${elementName}"]`,
        );
      elements.set(elementName, radioButtons);
    });
    return elements;
  }

  /**
   * Validate that all required elements are available
   */
  private validateElementsAvailability(): boolean {
    const validators = [
      () => this.checkElementsAvailable(this.elements.sections, "sections"),
      () =>
        this.checkElementsAvailable(this.elements.inputFields, "input fields"),
      () =>
        this.checkElementsAvailable(
          this.elements.radioButtons,
          "radio buttons",
        ),
      () => this.checkElementsAvailable(this.elements.buttons, "buttons"),
      () => this.checkElementsAvailable(this.elements.tabPanels, "tabpanels"),
    ];

    return validators.every((validator) => validator());
  }

  /**
   * Check if all elements in a collection are available
   */
  private checkElementsAvailable<T>(
    elements: Map<string, T | null>,
    elementType: string,
  ): boolean {
    const missingElements = Array.from(elements.entries())
      .filter(([, element]) => element === null)
      .map(([name]) => name);

    if (missingElements.length > 0) {
      console.error(`Missing ${elementType} in template:`, missingElements);
      return false;
    }
    return true;
  }

  /**
   * Initialize the registration process
   */
  private initialize(): void {
    this.registerEventListeners();
    this.setupStepValidations();
  }

  /**
   * Register global event listeners
   */
  private registerEventListeners(): void {
    const emailInput = this.elements.inputFields.get("email");
    emailInput?.addEventListener("input", () =>
      this.resetEmailValidation(emailInput),
    );

    const countryCodeB2B = this.elements.inputFields.get("b2b_countryCode");
    const countryCodeB2C = this.elements.inputFields.get("b2c_countryCode");
    countryCodeB2B?.addEventListener("change", () =>
      this.setAllInputValidations(),
    );
    countryCodeB2C?.addEventListener("change", () =>
      this.setAllInputValidations(),
    );

    document.addEventListener("input.country-codes-ready", () => {
      this.setAllInputValidations();
    });

    // Handle captcha completion and enable buttons if complete. Also set the default text, because the text is set to "Wait for captcha to complete ..." in the beginning
    document.addEventListener("ssi.captcha-complete", () => {
      this.elements.buttons.forEach((button) => {
        const alternateText = button.getAttribute("data-text");
        button.textContent = alternateText ?? button.textContent;
        button.disabled = false;
      });
    });

    // State change from captcha, disable all buttons and set text to "Wait for captcha to complete ..."
    document.addEventListener("ssi.captcha-statechange", () => {
      this.elements.buttons.forEach((button) => {
        const alternateText = button.getAttribute("data-captcha-text");
        button.textContent =
          alternateText ?? "Wait for captcha to complete ...";
        button.disabled = true;
      });
    });
  }

  /**
   * Setup validation for all registration steps
   */
  private setupStepValidations(): void {
    this.setupStep1Validation();
    this.setupStep2Validation();
    this.setupStep3Validation();
  }

  /**
   * Setup validation for step 1 (email and password)
   */
  private setupStep1Validation(): void {
    const { inputFields, buttons, sections } = this.elements;
    const emailInput = inputFields.get("email");
    const passwordInput = inputFields.get("password");
    const passwordRepeatInput = inputFields.get("passwordConfirmation");
    const submitButton = buttons.get("step1-submit");
    const step2Section = sections.get("step2");

    submitButton?.addEventListener("click", async (event) => {
      event.preventDefault();

      if (passwordInput?.value !== passwordRepeatInput?.value) {
        const customPasswordMismatchMessage = passwordRepeatInput?.getAttribute(
          "data-password-field-mismatch",
        );
        passwordRepeatInput?.setCustomValidity(
          customPasswordMismatchMessage ?? "Passwords do not match",
        );
        passwordRepeatInput?.reportValidity();
        return false;
      } else {
        passwordRepeatInput?.setCustomValidity("");
      }

      if (
        !this.validateInputs([emailInput, passwordInput, passwordRepeatInput])
      ) {
        return;
      }

      const isReadyForStep2 = await this.checkEmailAvailability();
      if (isReadyForStep2) {
        this.removeElementParent(submitButton);
        step2Section?.removeAttribute("hidden");
      }
    });
  }

  /**
   * Setup validation for step 2 (personal information)
   */
  private setupStep2Validation(): void {
    const { inputFields, buttons, sections } = this.elements;
    const titleInput = inputFields.get("title");
    const firstNameInput = inputFields.get("givenName");
    const lastNameInput = inputFields.get("familyName");
    const telephoneInput = inputFields.get("phone");
    const submitButton = buttons.get("step2-submit");
    const step3Section = sections.get("step3");

    submitButton?.addEventListener("click", (event) => {
      event.preventDefault();

      if (
        !this.validateInputs([
          titleInput,
          firstNameInput,
          lastNameInput,
          telephoneInput,
        ])
      ) {
        return;
      }

      this.removeElementParent(submitButton);
      step3Section?.removeAttribute("hidden");
    });
  }

  /**
   * Setup validation for step 3 (address and completion)
   */
  private setupStep3Validation(): void {
    const { buttons, tabPanels } = this.elements;
    const submitButton = buttons.get("step3-submit");
    const tabPanelB2B = tabPanels.get("panel-1");
    const tabPanelB2C = tabPanels.get("panel-2");

    submitButton?.addEventListener("click", async (event) => {
      event.preventDefault();

      const isB2BActive = !tabPanelB2B?.classList.contains("hidden");
      const isB2CActive = !tabPanelB2C?.classList.contains("hidden");

      this.handlePanelRequiredFields(isB2BActive, isB2CActive);

      if (!this.form.reportValidity()) {
        return;
      }

      await this.submitRegistration(isB2BActive, isB2CActive);
    });
  }

  /**
   * Validate array of input elements
   */
  private validateInputs(inputs: Array<HTMLInputElement | undefined>): boolean {
    return inputs.every((input) => input?.reportValidity() ?? false);
  }

  /**
   * Remove parent element of given element
   */
  private removeElementParent(element: HTMLElement | null): void {
    element?.parentElement?.remove();
  }

  /**
   * Set validation patterns for all inputs based on country codes
   */
  private setAllInputValidations(): void {
    const { inputFields } = this.elements;
    const countryCodeB2B = inputFields.get("b2b_countryCode")?.value ?? "";
    const countryCodeB2C = inputFields.get("b2c_countryCode")?.value ?? "";

    this.setInputPattern(
      inputFields.get("password"),
      this.validationPatterns.password,
    );
    this.setInputPattern(
      inputFields.get("passwordConfirmation"),
      this.validationPatterns.password,
    );

    // Set patterns for B2B fields
    this.setInputPattern(
      inputFields.get("b2b_street"),
      this.getPattern("street", countryCodeB2B),
    );
    this.setInputPattern(
      inputFields.get("b2b_postalCode"),
      this.getPattern("postalCode", countryCodeB2B),
    );
    this.setInputPattern(
      inputFields.get("b2b_vatId"),
      this.getPattern("vatId", countryCodeB2B),
    );
    this.setInputPattern(
      inputFields.get("b2b_customerNumber"),
      this.validationPatterns.customerNumber,
    );

    // Set patterns for B2C fields
    this.setInputPattern(
      inputFields.get("b2c_street"),
      this.getPattern("street", countryCodeB2C),
    );
    this.setInputPattern(
      inputFields.get("b2c_postalCode"),
      this.getPattern("postalCode", countryCodeB2C),
    );
    this.setInputPattern(
      inputFields.get("b2c_customerNumber"),
      this.validationPatterns.customerNumber,
    );

    // Set phone pattern
    this.setInputPattern(
      inputFields.get("phone"),
      this.validationPatterns.phone,
    );
  }

  /**
   * Get a validation pattern for specific type and country
   */
  private getPattern(
    type: keyof Omit<ValidationPatterns, "customerNumber" | "phone">,
    country: string,
  ): string {
    const patterns = this.validationPatterns[type];
    // @ts-expect-error Geht so klar
    return patterns[country] ?? patterns.default ?? "";
  }

  /**
   * Set pattern attribute on input element
   */
  private setInputPattern<TElement>(input: TElement, pattern: string): void {
    if (input instanceof HTMLInputElement) {
      input.pattern = pattern;
    } else if (input instanceof HTMLSelectElement) {
      input.setAttribute("pattern", pattern);
    }
  }

  /**
   * Reset email input validation state
   */
  private resetEmailValidation(emailInput: HTMLInputElement): void {
    emailInput.setCustomValidity("");
    emailInput.reportValidity();
  }

  /**
   * Reset captcha by dispatching custom event
   */
  private resetCaptcha(): void {
    document.dispatchEvent(new CustomEvent("registration.reset-captcha"));
  }

  /**
   * Check email availability and attempt login
   */
  private async checkEmailAvailability(): Promise<boolean> {
    const formData = new FormData(this.form);

    const request: RequestInit = { method: "POST", body: formData };
    const response = await tryCatch(
      fetch("/user/registration/email-available", request),
    );

    if (response.error) {
      console.error("Error checking email availability:", response.error);
      return false;
    }

    // Handle redirect if location header is present
    const locationHeader = response.data?.headers.get("Location");
    if (locationHeader) {
      window.location.replace(locationHeader);
      return false;
    }

    this.resetCaptcha();

    const responseStatus =
      (await response.data?.text()) as EMAIL_AVAILABLE_RESPONSE_CODES;
    this.handleEmailValidityResponse(responseStatus);

    return responseStatus === EMAIL_AVAILABLE_RESPONSE_CODES.OK;
  }

  /**
   * Handle email validity response from server
   */
  private handleEmailValidityResponse(
    status: EMAIL_AVAILABLE_RESPONSE_CODES,
    emailInput = this.elements.inputFields.get("email"),
  ): void {
    if (status === EMAIL_AVAILABLE_RESPONSE_CODES.NOT_AVAILABLE) {
      const emailExistingErrorMessage = emailInput?.getAttribute(
        "data-custom-email-existing-error-message",
      );
      emailInput?.setCustomValidity(
        emailExistingErrorMessage ?? "Email already exists",
      );
      emailInput?.reportValidity();
    } else {
      emailInput?.setCustomValidity("");
    }
  }

  /**
   * Handle required fields based on an active panel
   */
  private handlePanelRequiredFields(
    isB2BActive: boolean,
    isB2CActive: boolean,
  ): void {
    const b2bRequiredFieldNames = [
      "b2b_name",
      "b2b_street",
      "b2b_postalCode",
      "b2b_city",
      "b2b_countryCode",
      "b2b_industryName",
    ];

    const b2cRequiredFieldNames = [
      "b2c_street",
      "b2c_postalCode",
      "b2c_city",
      "b2c_countryCode",
    ];

    const b2bRequiredFields = b2bRequiredFieldNames
      .map((field) => this.elements.inputFields.get(field))
      .filter(Boolean) as Array<HTMLInputElement>;
    const b2cRequiredFields = b2cRequiredFieldNames
      .map((field) => this.elements.inputFields.get(field))
      .filter(Boolean) as Array<HTMLInputElement>;

    // Remove required attribute from all fields
    b2bRequiredFields.forEach((input) => input.removeAttribute("required"));
    b2cRequiredFields.forEach((input) => input.removeAttribute("required"));
    if (isB2BActive && !isB2CActive) {
      b2bRequiredFields.forEach((input) => input.setAttribute("required", ""));
    }
    if (isB2CActive && !isB2BActive) {
      b2cRequiredFields.forEach((input) => input.setAttribute("required", ""));
    }
  }

  /**
   * Submit the registration form
   */
  private async submitRegistration(
    isB2BActive: boolean,
    isB2CActive: boolean,
  ): Promise<void> {
    this.handleSmasuGotoUrl(); // Manipulate Goto URL if SMASU password is present

    const formData = this.buildFormData(isB2BActive, isB2CActive);
    const request: RequestInit = {
      method: "POST",
      body: formData,
    };
    const response = await tryCatch(
      fetch("/user/registration?version=v2", request),
    );

    if (this.isSmasuRegistration && response.data?.status === 200) {
      const loginFormData = new FormData();
      loginFormData.append(
        "email",
        this.elements.inputFields.get("email")?.value ?? "",
      );
      loginFormData.append(
        "password",
        this.elements.inputFields.get("password")?.value ?? "",
      );

      const gotoUrl = this.elements.inputFields.get("gotoUrl")?.value ?? "";
      const gotoUrlObject = new URL(gotoUrl);
      const relativeGotoUrl = gotoUrlObject.pathname + gotoUrlObject.search;
      const baseUrl = this.elements.inputFields.get("baseUrl")?.value as string;

      const loginParams = new URLSearchParams();
      loginParams.append("gotoUrl", relativeGotoUrl);

      const loginResponse = await tryCatch(
        fetch(`/user/login?${loginParams.toString()}`, {
          method: "POST",
          body: loginFormData,
          redirect: "manual",
        }),
      );

      if (loginResponse.data?.type === "opaqueredirect") {
        return window.location.replace(
          new URL(this.elements.inputFields.get("gotoUrl")?.value ?? ""),
        );
      } else {
        return window.location.replace(
          new URL(`/user/login?${loginParams.toString()}`, baseUrl),
        );
      }
    }

    if (response.data?.status !== 200) {
      const body = await response.data?.json();
      if (body.fieldErrors) {
        const alertRegistration = document.querySelector(
          "[data-alert-registration]",
        );
        const message = body.fieldErrors
          .map(
            (error: { field: string; message: string }) =>
              `${error.field}: ${error.message}`,
          )
          .join("\n");
        const alertContent = alertRegistration?.querySelector(
          "[data-alert-content]",
        );
        if (alertContent) {
          alertContent.textContent = message;
        }
        alertRegistration?.removeAttribute("hidden");
        return;
      } else {
        console.error("Registration failed:", response.data);
        return;
      }
    }

    if (response.data?.url) {
      return window.location.replace(new URL(response.data.url));
    }

    const redirectUrl =
      this.elements.inputFields.get("gotoUrl")?.value ??
      "/user/login?created=true";
    window.location.replace(redirectUrl);
  }

  /**
   * Build FormData for registration submission
   */
  private buildFormData(isB2BActive: boolean, isB2CActive: boolean): FormData {
    const formData = new FormData();
    const { inputFields, radioButtons } = this.elements;

    // Step 1 - Authentication data
    this.appendFormField(
      formData,
      "login.email",
      inputFields.get("email")?.value,
    );
    this.appendFormField(
      formData,
      "identity.password",
      inputFields.get("password")?.value,
    );
    this.appendFormField(
      formData,
      "identity.passwordConfirmation",
      inputFields.get("passwordConfirmation")?.value,
    );
    this.appendFormField(
      formData,
      "frc-captcha-response",
      inputFields.get("frc-captcha-response")?.value,
    );

    // Step 2 - Personal data
    this.appendFormField(
      formData,
      "customerProfile",
      inputFields.get("customerProfile")?.value,
    );
    this.appendFormField(
      formData,
      "identity.customerProfile",
      inputFields.get("customerProfile")?.value,
    );
    this.appendFormField(
      formData,
      "identity.title",
      this.getSelectedRadioValue(radioButtons, "title"),
    );
    this.appendFormField(
      formData,
      "identity.givenName",
      inputFields.get("givenName")?.value,
    );
    this.appendFormField(
      formData,
      "identity.familyName",
      inputFields.get("familyName")?.value,
    );
    this.appendFormField(
      formData,
      "identity.phone",
      inputFields.get("phone")?.value,
    );

    // Step 3 - Address data
    if (isB2BActive && !isB2CActive) {
      this.appendB2BFields(formData);
    } else if (isB2CActive && !isB2BActive) {
      this.appendB2CFields(formData);
    }

    // Additional fields
    this.appendBooleanField(
      formData,
      "identity.terms",
      inputFields.get("terms")?.checked,
    );
    this.appendBooleanField(
      formData,
      "subscribeNewsletter",
      inputFields.get("subscribeNewsletter")?.checked,
    );
    this.appendFormField(
      formData,
      "gotoUrl",
      inputFields.get("gotoUrl")?.value,
    );

    // Static fields
    this.appendStaticFields(formData);

    return formData;
  }

  /**
   * Append form field with fallback to empty string
   */
  private appendFormField(
    formData: FormData,
    key: string,
    value?: string,
  ): void {
    formData.append(key, value ?? "");
  }

  /**
   * Append boolean field as string
   */
  private appendBooleanField(
    formData: FormData,
    key: string,
    value?: boolean,
  ): void {
    formData.append(key, value ? "1" : "0");
  }

  /**
   * Get selected radio button value
   */
  private getSelectedRadioValue(
    radioButtons: Map<string, NodeListOf<HTMLInputElement>>,
    name: string,
  ): string {
    const radios = radioButtons.get(name);
    const selectedRadio = Array.from(radios ?? []).find(
      (radio) => radio.checked,
    );
    return selectedRadio?.value ?? "";
  }

  /**
   * Append B2B specific fields to form data
   */
  private appendB2BFields(formData: FormData): void {
    const { inputFields, radioButtons } = this.elements;

    // Organisation data
    this.appendFormField(
      formData,
      "organisation.name",
      inputFields.get("b2b_name")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.name2",
      inputFields.get("b2b_name2")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.vatId",
      inputFields.get("b2b_vatId")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.nameAdditional",
      inputFields.get("b2b_nameAdditional")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.industryName",
      inputFields.get("b2b_industryName")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.industrySsiCode",
      this.getIndustryCode("data-industry-ssi-code"),
    );
    this.appendFormField(
      formData,
      "organisation.industryNaceCode",
      this.getIndustryCode("data-industry-nace-code"),
    );
    this.appendFormField(
      formData,
      "organisation.customerNumber",
      inputFields.get("b2b_customerNumber")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.countryCode",
      inputFields.get("b2b_countryCode")?.value,
    );

    // Address data
    this.appendFormField(
      formData,
      "addressList[0].name",
      inputFields.get("b2b_name")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].name2",
      inputFields.get("b2b_name2")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].nameAdditional",
      inputFields.get("b2b_nameAdditional")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].title",
      this.getSelectedRadioValue(radioButtons, "title"),
    );
    this.appendFormField(
      formData,
      "addressList[0].givenName",
      inputFields.get("givenName")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].familyName",
      inputFields.get("familyName")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].street",
      inputFields.get("b2b_street")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].postalCode",
      inputFields.get("b2b_postalCode")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].city",
      inputFields.get("b2b_city")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].countryCode",
      inputFields.get("b2b_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "identity.countryCode",
      inputFields.get("b2b_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "smasuPassword",
      inputFields.get("b2b_smasuPassword")?.value,
    );
  }

  /**
   * Append B2C specific fields to form data
   */
  private appendB2CFields(formData: FormData): void {
    const { inputFields, radioButtons } = this.elements;

    // Address data
    this.appendFormField(
      formData,
      "addressList[0].street",
      inputFields.get("b2c_street")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].nameAdditional",
      inputFields.get("b2c_nameAdditional")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].postalCode",
      inputFields.get("b2c_postalCode")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].city",
      inputFields.get("b2c_city")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].countryCode",
      inputFields.get("b2c_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "identity.countryCode",
      inputFields.get("b2c_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "countryCode",
      inputFields.get("b2c_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.countryCode",
      inputFields.get("b2c_countryCode")?.value,
    );
    this.appendFormField(
      formData,
      "organisation.customerNumber",
      inputFields.get("b2c_customerNumber")?.value,
    );

    // Personal data
    this.appendFormField(
      formData,
      "addressList[0].title",
      this.getSelectedRadioValue(radioButtons, "title"),
    );
    this.appendFormField(
      formData,
      "addressList[0].givenName",
      inputFields.get("givenName")?.value,
    );
    this.appendFormField(
      formData,
      "addressList[0].familyName",
      inputFields.get("familyName")?.value,
    );
  }

  /**
   * Get industry code from select element attribute
   */
  private getIndustryCode(attributeName: string): string {
    const select = this.elements.inputFields.get(
      "b2b_industryName",
    ) as unknown as HTMLSelectElement;
    const selectedOption = select?.options[select.selectedIndex];
    return selectedOption?.getAttribute(attributeName) ?? "";
  }

  /**
   * Append static form fields
   */
  private appendStaticFields(formData: FormData): void {
    const staticFields = {
      "addressList[0].delivery": "1",
      "addressList[0].billing": "1",
      "addressList[0].primaryBilling": "1",
      "addressList[0].primaryDelivery": "1",
      "addressList[0].main": "1",
      "identity.birthday": "1900-01-01",
      isGuest: "0",
      isUpdateMode: "0",
    };

    Object.entries(staticFields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  /**
   * Handle SMASU Goto URL by appending smasuPassword if available
   */
  private handleSmasuGotoUrl(): void {
    const gotoUrl = this.elements.inputFields.get("gotoUrl");
    const baseUrl = this.elements.inputFields.get("baseUrl")?.value as string;
    const smasuPasswordField =
      this.elements.inputFields.get("b2b_smasuPassword");

    if (
      smasuPasswordField &&
      smasuPasswordField.value.length > 0 &&
      gotoUrl &&
      baseUrl
    ) {
      const newUrl = new URL(gotoUrl.value, baseUrl);
      newUrl.searchParams.append("smasuPassword", smasuPasswordField.value);
      gotoUrl.value = newUrl.toString();

      this.isSmasuRegistration = true;
    }
  }
}

// Initialize registration when DOM is loaded
window.addEventListener("load", () => {
  RegistrationManager.create();
});
